import { action, computed, observable } from 'mobx'
import { type IValidationSchema, required, validationContext } from 'validx'
import { manageEach } from '@/features/legends/editor/collections'
import type { FilterType, IfRule } from '@/features/legends/filters/rules'
import { validateWhileEditing } from '@/features/legends/editor/validation'
import {
  FilterEditorViewModelBase,
  type FilterEditorViewModelOptions,
} from '@/features/legends/filters/FilterEditorViewModelBase'
import {
  type AnyFilterEditor,
  createFilter,
  hydrateFilter,
} from '@/features/legends/filters/filter-registry'
import { groupFilterTypes } from '@/features/legends/filters/filter-types'
import { validateObject } from '@/utils/validx-util'

export type IfBranch = 'conditionRule' | 'thenRule' | 'elseRule'

/**
 * If (a filter) then (another filter) else (a third filter).
 */
export class IfEditorViewModel extends FilterEditorViewModelBase<IfRule> {
  private readonly _schema: IValidationSchema<IfEditorViewModel> = {
    conditionRule: [required(), validateObject('Condition rule is invalid.')],
    thenRule: [required(), validateObject('Then rule is invalid.')],
    elseRule: [required(), validateObject('Else rule is invalid.')],
  }

  readonly type = 'If'

  @observable accessor conditionRule: AnyFilterEditor | null = null
  @observable accessor thenRule: AnyFilterEditor | null = null
  @observable accessor elseRule: AnyFilterEditor | null = null

  readonly validation = validationContext(this, this._schema)

  constructor(options: FilterEditorViewModelOptions) {
    super(options)
    this.lifecycle.manage(
      manageEach(this.lifecycle, () => [
        this.conditionRule,
        this.thenRule,
        this.elseRule,
      ]),
    )
    this.lifecycle.manage(validateWhileEditing(this, this._schema))
  }

  /**
   * The filter types a branch can be set to.
   */
  @computed
  get availableFilterTypes() {
    return groupFilterTypes(this.scope)
  }

  /**
   * Sets the branch to an empty filter of the type.
   */
  @action.bound
  setFilter(branch: IfBranch, type: FilterType) {
    this[branch] = createFilter(type, this._filterOptions)
  }

  /**
   * Clears the branch.
   */
  @action.bound
  removeFilter(branch: IfBranch) {
    this[branch] = null
  }

  /** @inheritDoc */
  @action
  hydrate(rule: IfRule) {
    const parse = (branch: IfBranch) => {
      const data = rule[branch]
      return data ? hydrateFilter(data, this._filterOptions) : null
    }
    this.conditionRule = parse('conditionRule')
    this.thenRule = parse('thenRule')
    this.elseRule = parse('elseRule')
  }

  /** @inheritDoc */
  toJSON(): IfRule {
    return {
      type: this.type,
      conditionRule: this.conditionRule?.toJSON() ?? null,
      thenRule: this.thenRule?.toJSON() ?? null,
      elseRule: this.elseRule?.toJSON() ?? null,
    }
  }
}
